/**
 * Client-side mirror of the server's record-deletion policy.
 *
 * The database is the enforcement layer (can_soft_delete_record +
 * records_guard_deletion trigger, migration 20260718090000); this module only
 * decides what UI to show. Keep the two in lockstep — the SQL function is the
 * source of truth:
 *
 *   - Super User: always.
 *   - No role / viewer: never (viewer even if mislisted in delete_record_roles).
 *   - Role must be in config.delete_record_roles (missing key = ["admin"]).
 *   - Admin (when listed): exempt from the two gates below.
 *   - Status gate: non-admin roles need allow_delete_submitted_records for
 *     records beyond the draft/in_progress/needs_revision trio.
 *   - Ownership gate: non-admin roles need allow_delete_others_records for
 *     records they neither own nor created.
 *
 * Restore is admin / Super User only (04_records_patients.md §4.7.3), and the
 * "show deleted" toggle mirrors the DB's can_see_deleted().
 */
import type { ProjectRole } from '@/features/auth/context/project-role-context'

export interface DeletionPolicyConfig {
  delete_record_roles?: string[]
  allow_delete_submitted_records?: boolean
  allow_delete_others_records?: boolean
}

export const UNSUBMITTED_RECORD_STATUSES = [
  'draft',
  'in_progress',
  'needs_revision',
] as const

export interface DeletableRecordFields {
  status: string
  is_deleted: boolean
  owner_id: string | null
  created_by: string | null
}

function allowedDeleteRoles(config: DeletionPolicyConfig | null | undefined): string[] {
  const roles = config?.delete_record_roles
  return Array.isArray(roles) ? roles : ['admin']
}

export function canDeleteRecord(args: {
  role: ProjectRole
  userId: string | null | undefined
  record: DeletableRecordFields
  config: DeletionPolicyConfig | null | undefined
}): boolean {
  const { role, userId, record, config } = args
  if (record.is_deleted) return false
  if (role === 'super_user') return true
  if (!role || role === 'viewer') return false
  if (!allowedDeleteRoles(config).includes(role)) return false
  if (role === 'admin') return true

  const isUnsubmitted = (UNSUBMITTED_RECORD_STATUSES as readonly string[]).includes(record.status)
  if (!isUnsubmitted && !(config?.allow_delete_submitted_records ?? false)) return false

  const isOwn =
    userId != null && (record.owner_id === userId || record.created_by === userId)
  if (!isOwn && !(config?.allow_delete_others_records ?? false)) return false

  return true
}

export function canRestoreRecord(role: ProjectRole): boolean {
  return role === 'admin' || role === 'super_user'
}

/** Mirrors the DB's can_see_deleted(): super, admin, or any listed delete role. */
export function canSeeDeletedRecords(
  role: ProjectRole,
  config: DeletionPolicyConfig | null | undefined,
): boolean {
  if (role === 'super_user' || role === 'admin') return true
  if (!role || role === 'viewer') return false
  return allowedDeleteRoles(config).includes(role)
}
